import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { PrimaryButtonInline, NamedLink } from '../../components';

import css from './MediaHeroSection.module.css';

const MediaHeroSection = props => {
  const {
    sectionId,
    className,
    backgroundColorDesktop = '#000', // Pink background from design
    backgroundColorMobile = '#000',
    titleText,
    subtitleText,
    topText,
    topSubText,
    bottomText,
    bottomSubText,
    mediaType = 'image',
    mediaSrc = null,
    mediaDesktopSrc = null, // Desktop video/image source
    mediaMobileSrc = null, // Mobile video/image source
    backgroundImage = null, // New prop for background image
    backgroundImageMobile = null, // New prop for mobile background image
    backgroundVideo = false, // New prop to enable full-width background video
    mediaAlt = 'Klarna cactus video',
    primaryButtonText = 'Shop listings',
    primaryButtonLink = '/s',
    secondaryButtonText = 'Sign up',
    secondaryButtonLink = '/signup',
    onPrimaryClick,
    onSecondaryClick,
  } = props;

  // State to track current viewport size
  const [isMobile, setIsMobile] = React.useState(false);

  // Set up window resize listener to detect mobile/desktop
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    // Initial check
    checkScreenSize();

    // Add resize event listener
    window.addEventListener('resize', checkScreenSize);

    // Clean up
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const rootClasses = classNames(css.root, className);

  const renderMedia = () => {
    // Determine which media source to use based on screen size
    const currentMediaSrc = isMobile
      ? mediaMobileSrc || mediaSrc
      : mediaDesktopSrc || mediaSrc;

    if (!currentMediaSrc) {
      // Placeholder when no media is provided
      return (
        <div className={css.mediaPlaceholder}>
          <div className={css.placeholderContent}>
            <span className={css.placeholderText}>Media</span>
            <span className={css.placeholderSubtext}>{mediaType}</span>
          </div>
        </div>
      );
    }

    if (mediaType === 'video') {
      return (
        <div className={css.mediaBox}>
          <video
            className={css.media}
            autoPlay
            muted
            loop
            playsInline
            poster={mediaAlt}
            src={currentMediaSrc}
            key={currentMediaSrc} // Key helps ensure video reloads when source changes
            style={{
              objectFit: 'contain',
              height: 'auto',
              width: '100%',
              objectPosition: 'center',
              borderRadius: 0,
              background: isMobile ? 'white' : 'transparent',
            }}
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    return (
      <div className={css.mediaBox}>
        <img
          className={css.media}
          src={currentMediaSrc}
          alt={mediaAlt}
          loading="lazy"
        />
      </div>
    );
  };

  const handlePrimaryClick = () => {
    if (onPrimaryClick) {
      onPrimaryClick();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSecondaryClick = () => {
    if (onSecondaryClick) {
      onSecondaryClick();
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Determine if we're using a background image or color
  const useBackgroundImage = backgroundImage || backgroundImageMobile;
  const currentBackgroundImage =
    isMobile && backgroundImageMobile ? backgroundImageMobile : backgroundImage;

  // Determine if we're using a background video
  const useBackgroundVideo =
    backgroundVideo &&
    mediaType === 'video' &&
    (mediaSrc || mediaDesktopSrc || mediaMobileSrc);
  const currentBackgroundVideoSrc = isMobile
    ? mediaMobileSrc || mediaSrc
    : mediaDesktopSrc || mediaSrc;

  const sectionContent = (
    <section
      id={sectionId}
      className={rootClasses}
      style={{
        '--background-color-desktop': backgroundColorDesktop,
        '--background-color-mobile': backgroundColorMobile,
        '--is-mobile': isMobile ? '1' : '0',
        '--aspect-ratio': isMobile ? '1.78' : '0.8',
        backgroundImage: useBackgroundImage
          ? `url(${currentBackgroundImage})`
          : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {useBackgroundVideo && (
        <video
          className={css.backgroundVideo}
          autoPlay
          muted
          loop
          playsInline
          src={currentBackgroundVideoSrc}
          style={{
            position: 'absolute',
            top: '-1px',
            left: '-1px',
            width: 'calc(100% + 2px)',
            height: 'calc(100% + 2px)',
            objectFit: 'cover',
            zIndex: 0,
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
          }}
        >
          Your browser does not support the video tag.
        </video>
      )}
      <div
        className={css.bannerContent}
        style={
          useBackgroundImage || useBackgroundVideo
            ? {
                background: 'transparent',
                position: 'relative',
                border: 'none',
                zIndex: 1,
              }
            : undefined
        }
      >
        {isMobile ? (
          // Mobile layout - stacked with image on top
          <>
            {!useBackgroundImage && !useBackgroundVideo && (
              <div className={css.mobileImageContainer}>
                <NamedLink name="NewListingPage">{renderMedia()}</NamedLink>
              </div>
            )}
            {/* <div className={css.mobileTextContent}>
              <h1 className={css.headline}>
                {titleText || "LET'S MAKE 2026"}
                <span
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    height: '0.3rem',
                  }}
                />
                {'EVEN MORE ICONIC.'}
              </h1>
              <div className={css.buttonRow}>
                <NamedLink
                  className={css.primaryButton}
                  name={primaryButtonLink || 'SearchPage'}
                >
                  {primaryButtonText}
                </NamedLink>
                <NamedLink
                  className={css.outlineButton}
                  name={secondaryButtonLink || 'SignupPage'}
                >
                  {secondaryButtonText}
                </NamedLink>
              </div>
            </div>*/}
          </>
        ) : (
          // Desktop layout - side by side
          <>
            <div className={css.left}>
              <h1 className={css.headline}>
                {titleText || 'SELL YOUR ICON'}
                {/* <span
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    height: '0.8rem',
                  }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    display: 'block',
                    height: '0.8rem',
                  }}
                />
                {'MORE ICONIC.'}*/}
              </h1>
              <p className={css.description}>
                {topText ? (
                  `${topText} ${topSubText || ''} ${bottomText ||
                    ''} ${bottomSubText || ''}`
                ) : (
                  <>
                    <span>List in minutes, we'll handle the rest.</span>
                  </>
                )}
              </p>
              <div className={css.buttonRow}>
                <NamedLink
                  className={css.primaryButton}
                  name={primaryButtonLink || 'SearchPage'}
                >
                  {primaryButtonText}
                </NamedLink>
                <NamedLink
                  className={css.outlineButton}
                  name={secondaryButtonLink || 'SignupPage'}
                >
                  {secondaryButtonText}
                </NamedLink>
              </div>
            </div>
            {!useBackgroundImage && !useBackgroundVideo && (
              <div className={css.right}>{renderMedia()}</div>
            )}
          </>
        )}
      </div>
    </section>
  );

  if (isMobile && useBackgroundImage) {
    return (
      <NamedLink name="NewListingPage" style={{ display: 'block' }}>
        {sectionContent}
      </NamedLink>
    );
  }

  return sectionContent;
};

MediaHeroSection.propTypes = {
  sectionId: PropTypes.string,
  className: PropTypes.string,
  backgroundColorDesktop: PropTypes.string,
  backgroundColorMobile: PropTypes.string,
  titleText: PropTypes.string,
  subtitleText: PropTypes.string,
  topText: PropTypes.string,
  topSubText: PropTypes.string,
  bottomText: PropTypes.string,
  bottomSubText: PropTypes.string,
  mediaType: PropTypes.oneOf(['image', 'video', 'gif']),
  mediaSrc: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  mediaDesktopSrc: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  mediaMobileSrc: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  backgroundImage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  backgroundImageMobile: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  backgroundVideo: PropTypes.bool,
  mediaAlt: PropTypes.string,
  primaryButtonText: PropTypes.string,
  primaryButtonLink: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  secondaryButtonLink: PropTypes.string,
  onPrimaryClick: PropTypes.func,
  onSecondaryClick: PropTypes.func,
};

export default MediaHeroSection;
